import React, {ReactElement} from "react";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {getTranslations} from "../../i18n/translations";
import { WineMenuFormat } from "lib/api/pdfGenerator";

export interface CoverPageProps {

}

export function CoverPagePreview(props: CoverPageProps): ReactElement | null {
    const dispatch = useAppDispatch();
    const coverPageEditorOpen = useAppSelector(menuEditorSelectors.coverPageEditorActive);
    const document = useAppSelector(menuEditorSelectors.document);
    if (document == null || document.coverPage == null) {
        return null;
    }

    const toggleCoverPageEditor = () => dispatch(menuEditorActions.setCoverPageEditorActive(!coverPageEditorOpen));

    return (
      <div className={`bg-white flex flex-col relative overflow-hidden
          ${document.format === 'a5'      ? 'w-[152mm] h-[213.75mm]' :
            document.format === 'a4-half' ? 'w-[109mm] h-[300.75mm]' :
                                            'w-[214mm] h-[301mm]'}`}
          style={{pageBreakAfter: 'always'}}
          onClick={() => toggleCoverPageEditor()}
      >
          {{
              1: <CoverPageVariant1
                format={document.format as WineMenuFormat}
                imgUrl={document.coverPage.coverImageUrl}
                title={document.coverPage.title}
                logoImgUrl={document.coverPage.logoImageUrl}
              />,
              2: <CoverPageVariant2
                format={document.format as WineMenuFormat}
                imgUrl={document.coverPage.coverImageUrl}
                title={document.coverPage.title}
                logoImgUrl={document.coverPage.logoImageUrl}
                />,
              3: <CoverPageVariant3
                format={document.format as WineMenuFormat}
                imgUrl={document.coverPage.coverImageUrl}
                title={document.coverPage.title}
                logoImgUrl={document.coverPage.logoImageUrl}
                />,
          }[document.coverPage.variant || '1']}
      </div>
    );
}

interface CoverPageVariantProps {
    format: WineMenuFormat
    imgUrl: string | null,
    title: string | null,
    logoImgUrl: string | null,
}

function CoverPageVariant1(props: CoverPageVariantProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const lang = document?.language || 'de';
    const headerFontSize = lang === 'fr' ? 76
        : lang === 'it' ? 90
        : 100;
    const labelText = getTranslations(lang)['editor.coverPage.winemenu'];

    return (
        <>
            <div className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden">
                <img
                    src={props.imgUrl || ''}
                    className="w-full h-full object-cover object-center"
                    alt="Cover not found..."
                />
            </div>
            <div className="absolute top-0 right-full w-[300mm] transform rotate-[270deg] origin-top-right text-right pt-[10mm] pr-[20mm]">
                <h2 className={"text-white uppercase"} style={{fontSize: `${headerFontSize}px`, textShadow: '1px 1px 20px rgba(0,0,0,0.6)'}}>
                    {labelText}
                </h2>
                <h1 className="text-[36px] text-white" style={{textShadow: '1px 1px 20px rgba(0,0,0,0.6)'}}>
                    {props.title}
                </h1>
            </div>
            {props.logoImgUrl &&
                <div className="bg-white absolute bottom-0 left-0 w-full h-[150px] flex justify-end items-center px-[20mm]">
                    <img src={props.logoImgUrl} className="h-[18mm]" alt={props.logoImgUrl} />
                </div>
            }
        </>
    );
}

function CoverPageVariant2(props: CoverPageVariantProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const isA4 = document?.format === "a4",
        isHalfA4 = document?.format === "a4-half";

    const lang = document?.language || "de";
    const labelText = getTranslations(lang)['editor.coverPage.winemenu'];

    let headerFontSize = isA4 ? 80
        : isHalfA4 ? 60
        : 70;

    if (isHalfA4 && (lang === 'fr' || lang === 'it')) {
        headerFontSize = 50;
    }

    return (
        <>
            <div className="bg-white w-full text-center pt-[157px] pb-[10mm] flex flex-col items-center">
                <h2 className={`text-black uppercase border-b-8 border-primary`} style={{
                    lineHeight: '84px',
                    fontSize: `${headerFontSize}px`
                }}>
                    {labelText}
                </h2>
                <h1 className="text-[30px] text-black">
                    {props.title}
                </h1>
            </div>
            <div className="w-full flex-grow overflow-hidden">
                <img
                    src={props.imgUrl || ''}
                    className="w-full h-full object-cover object-center"
                    alt="Cover not found..."
                />
            </div>
            {props.logoImgUrl &&
                <div className="bg-white absolute top-0 left-0 w-full h-[120px] flex justify-center items-center pt-[20mm] px-[20mm]">
                    <img src={props.logoImgUrl} className="h-[18mm]" alt={props.logoImgUrl} />
                </div>
            }
        </>
    );
}

function CoverPageVariant3(props: CoverPageVariantProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const isHalfA4 = document?.format === "a4-half",
        isA5 = document?.format === "a5";

    const lang = document?.language || "de";

    let headerFontSize = lang === 'fr' ? 44 : 50;
    if (isHalfA4 && (lang === 'fr' || lang === 'it')) headerFontSize = 35;

    const labelText = getTranslations(lang)['editor.coverPage.winemenu'];

    const labelContainer = <div className="w-full py-[10mm] px-[20mm] flex flex-col text-center">
        <h2 className="uppercase" style={{fontSize: `${headerFontSize}px`}}>
            {labelText}
        </h2>
        <h1 className="text-[30px]">
            {props.title}
        </h1>
    </div>;

    return (
        <>
            {(isHalfA4||isA5) ?
                <>
                    {props.logoImgUrl &&
                    <div className="bg-white flex justify-center items-center py-[5mm]">
                        <img src={props.logoImgUrl} alt={props.logoImgUrl} style={{maxHeight: "18mm"}}/>
                    </div>
                    }
                    {!props.logoImgUrl && <div className="text-black">
                        {labelContainer}
                    </div>}
                    <div className="w-full flex-grow overflow-hidden relative">
                        {props.logoImgUrl && <div className="absolute w-full text-white"
                                                  style={{textShadow: '1px 1px 20px rgba(0,0,0,0.6)'}}>
                            {labelContainer}
                        </div>}
                        <img
                            src={props.imgUrl || ''}
                            className="w-full h-full object-cover object-center"
                            alt="Cover not found..."
                        />
                    </div>
                </>
                :
                <>
                    <div className="absolute top-0 left-0 right-0 bottom-0 overflow-hidden">
                        <img
                            src={props.imgUrl || ''}
                            className="object-cover object-center"
                            alt="Cover not found..."
                        />
                    </div>
                    <div className="bg-white absolute top-0 left-0 w-full py-[10mm] px-[20mm] flex flex-col">
                        <h2 className="text-black uppercase" style={{fontSize: `${headerFontSize}px`}}>
                            {labelText}
                        </h2>
                        <h1 className="text-[30px] text-black">
                            {props.title}
                        </h1>
                    </div>
                    {props.logoImgUrl &&
                    <div className="bg-white absolute flex justify-end items-center"
                         style={{
                             top: "10mm",
                             right: "20mm",
                             height: "68px"
                         }}
                    >
                        <img src={props.logoImgUrl} className="h-[18mm]" alt={props.logoImgUrl}/>
                    </div>
                    }
                </>
            }
        </>
    );
}